import React from "react";
import moment from "moment";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { RefreshButton } from "@/Common/Components/Buttons/RefreshButton";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { Gender, UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { ShareableGrowthChartModel } from "../../Models/GrowthCharts/ShareableGrowthChartModel";

interface GrowthChartsSettingsBarProps {
    patientContactId: string;
    isBornPremature: boolean;
    bornPrematureWeeks?: number;
    filterStartDate: Date;
    filterEndDate: Date;
    unit: UnitSystem;
    shareableItem: ShareableGrowthChartModel;
    showPortalButton: boolean;
    hasSelectedCharts: boolean;
    hasValidPatientGender: boolean;
    patientDateOfBirth: Date;
    onExportButtonClicked?: () => void;
    onFaxLabelClick?: () => void;
    onPortalButtonClick?: () => void;
}

class GrowthChartsSettingsBarState {
    public isBornPremature: boolean;
    public bornPrematureWeeks?: number;
    public dueDate?: Date;
    public selectedUnit: UnitSystem;
    public isRefreshButtonDisabled: boolean;
}

class GrowthChartsSettingsBar extends BaseReactComponent<GrowthChartsSettingsBarProps, GrowthChartsSettingsBarState> {
    private unitsSelectId = "growth-charts-settings-bar-units";
    private bornPrematureCheckboxInputId = "growth-charts-settings-bar-born-premature";
    private bornPrematureWeeksInputId = "growth-charts-settings-bar-premature-weeks";
    private debouncedBornPrematureValue: number;
    private debouncedBornPrematureTimeOut: NodeJS.Timeout;

    constructor(props: GrowthChartsSettingsBarProps) {
        super(props);

        const state = new GrowthChartsSettingsBarState();
        state.bornPrematureWeeks = props.bornPrematureWeeks < 40
            ? props.bornPrematureWeeks
            : null;
        state.isBornPremature = props.isBornPremature;
        state.selectedUnit = props.unit;

        this.state = state;
    }

    public componentDidMount(): void {
        this.debouncedBornPrematureValue = this.props.bornPrematureWeeks;
        this.updateBornPrematureWeeksAndDueDate();
    }

    public componentDidUpdate(prevProps: GrowthChartsSettingsBarProps): void {
        if (prevProps !== this.props) {
            this.debouncedBornPrematureValue = this.props.bornPrematureWeeks;
            this.debounceBornPrematureWeeks();
            this.mutateState(s => {
                s.isBornPremature = this.props.isBornPremature;
                s.selectedUnit = this.props.unit;
            });
        }
    }

    private setRefreshButtonDisabled(isDisabled: boolean): void {
        this.mutateState(s => {
            s.isRefreshButtonDisabled = isDisabled;
        });
    }

    public componentWillUnmount(): void {
        this.clearComponentTimeOut();
    }

    public render(): JSX.Element {
        return (
            <div className="settings-bar">
                <DateSelector
                    id="growth-charts-start-date"
                    date={this.props.filterStartDate}
                    onChangeCallback={date => {
                        sessionStore.dispatch(
                            LocalPatientOperations.GrowthCharts.SetStartDateFilter,
                            {
                                date: date
                            }
                        );
                    }}
                    dateFormat="yyyy-MM-dd"
                    valid={moment(this.props.filterEndDate) > moment(this.props.filterStartDate)}
                    isMonthPicker={false}
                    height={34}
                    width={126}
                    dataCy="growthCharts-startDate"
                />
                <DateSelector
                    id="growth-charts-end-date"
                    date={this.props.filterEndDate}
                    onChangeCallback={date => {
                        sessionStore.dispatch(LocalPatientOperations.GrowthCharts.SetEndDateFilter, {
                            date: date
                        });
                    }}
                    dateFormat="yyyy-MM-dd"
                    valid={moment(this.props.filterEndDate) > moment(this.props.filterStartDate)}
                    isMonthPicker={false}
                    height={34}
                    width={126}
                    dataCy="growthCharts-endDate"
                />
                <select
                    className="form-control myle-control"
                    id={this.unitsSelectId}
                    data-cy="growthCharts-unitSelect"
                    name={this.unitsSelectId}
                    onChange={e => { this.onUnitChange(e); }}
                    value={this.state.selectedUnit?.toString()}
                >
                    <option value={UnitSystem.Metric.toString()} key="metric">
                        {cacheStore.GetString("MetricUnitsLLabel")}
                    </option>
                    <option value={UnitSystem.Imperial.toString()} key="imperial">
                        {cacheStore.GetString("ImperialUnitsLLabel")}
                    </option>
                </select>
                {this.renderPatientErrors()}
                {this.renderBornPremature()}
                <div className="additional-options">
                    {this.props.hasSelectedCharts && (
                        <a onClick={this.props.onExportButtonClicked}>
                            {cacheStore.GetString("PrintLabel")}
                        </a>
                    )}
                    {this.props.hasSelectedCharts && (
                        <a onClick={() => this.props.onFaxLabelClick()}>
                            {cacheStore.GetString("SendFaxLabel")}
                        </a>
                    )}
                    <RefreshButton
                        isDisabled={this.state.isRefreshButtonDisabled}
                        onClick={() => {
                            this.setRefreshButtonDisabled(true);
                            patientStore.ActionsMedicalSummary.LoadPatientCumulativeProfile(
                                this.props.patientContactId,
                                null,
                                true,
                                true,
                                "GrowthChartsSettingsBar_ClickOnRefresh",
                                () => this.setRefreshButtonDisabled(false)
                            );
                        }}
                    />
                    {this.props.showPortalButton && this.props.hasSelectedCharts && (
                        <PortalStatusButton
                            disabled={false}
                            icon="share"
                            onClick={() => {
                                this.props.onPortalButtonClick && this.props.onPortalButtonClick();
                            }}
                            {...PortalHelper.getPortalButtonPropsForShareableItem(new ShareableGrowthChartModel(this.props.shareableItem))}
                        />
                    )}
                </div>
            </div>
        );
    }

    private renderBornPremature(): JSX.Element {
        if (!this.props.hasValidPatientGender || !this.props.patientDateOfBirth)
            return null;

        return (
            <div className="born-premature">
                <input
                    type="checkbox"
                    id={this.bornPrematureCheckboxInputId}
                    checked={this.state.isBornPremature}
                    data-cy="growthCharts-premature-check"
                    onChange={(e: any) => {
                        const checked = e.target.checked;
                        this.mutateState(s => {
                            s.isBornPremature = checked;
                        }, () => this.setPatientBornPrematureData());
                    }}
                />
                <label htmlFor={this.bornPrematureCheckboxInputId}>
                    {this.getBornPrematureLabelByPatientGender()}
                </label>
                {this.state.isBornPremature && (
                    <div className="growth-charts-due-date">
                        <label htmlFor={this.bornPrematureWeeksInputId}>
                            {cacheStore.GetString("EstimatedDueDate")}
                        </label>
                        <DateSelector
                            id="growth-charts-due-date"
                            date={this.state.isBornPremature ? this.state.dueDate : null}
                            onChangeCallback={date => {
                                this.mutateState(s => {
                                    s.dueDate = date;
                                }, () => this.setPatientBornPrematureDate());
                            }}
                            dateFormat="yyyy-MM-dd"
                            valid={this.isDueDateValid()}
                            isMonthPicker={false}
                            height={34}
                            width={126}
                            dataCy="growthCharts-dueDate"
                        />
                    </div>
                )}
            </div>
        );
    }

    private setPatientBornPrematureData(): void {
        sessionStore.dispatch(LocalPatientOperations.GrowthCharts.SetPatientBornPrematureData, {
            isBornPremature: this.state.isBornPremature,
            bornPrematureWeeks: this.state.bornPrematureWeeks
        });
    }

    private setPatientBornPrematureDate(): void {
        if (this.state.dueDate && !this.isDueDateValid())
            return;

        const bornPrematureWeeks = this.state.isBornPremature && this.isDueDateValid() ?
            this.calculateBornPrematureWeeks() :
            null;

        sessionStore.dispatch(LocalPatientOperations.GrowthCharts.SetPatientBornPrematureData, {
            isBornPremature: this.state.isBornPremature,
            bornPrematureWeeks: bornPrematureWeeks
        });
    }

    private calculateBornPrematureWeeks(): number {
        if (!this.state.dueDate || !this.props.patientDateOfBirth || !this.isDueDateValid())
            return null;

        return 40 - LuxonDateHelper.dateDiff(this.props.patientDateOfBirth, this.state.dueDate, "weeks", true);
    }

    private getDueDateByBornPremarureWeeks(bornPrematureWeeks: number): Date {
        if (!bornPrematureWeeks || !this.props.patientDateOfBirth)
            return null;

        return LuxonDateHelper.dateAdd(this.props.patientDateOfBirth, 40 - bornPrematureWeeks, "weeks", true);
    }

    private isDueDateValid(): boolean {
        return this.state.dueDate && new Date(this.state.dueDate) > new Date(this.props.patientDateOfBirth);
    }

    private onUnitChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        const unit = Number(e.target.value);
        this.mutateState(
            s => s.selectedUnit = unit,
            () => {
                sessionStore.dispatch(LocalPatientOperations.GrowthCharts.SetUnitFilter, {
                    unit: unit
                });
            }
        );
    }

    private getBornPrematureLabelByPatientGender(): string {
        const gender = MyleHelper.genderLookupToEnum(patientStore.getCurrentPatient()?.IndividualResponse?.GenderLookupId);

        switch (gender) {
            case Gender.Male:
                return cacheStore.GetString("MaleBornPrematureLabel");
            case Gender.Female:
                return cacheStore.GetString("FemaleBornPrematureLabel");
            case Gender.Unspecified:
            default:
                return cacheStore.GetString("BornPrematureLabel");
        }
    }

    private debounceBornPrematureWeeks(): void {
        this.clearComponentTimeOut();
        this.debouncedBornPrematureTimeOut = setTimeout(() => {
            this.updateBornPrematureWeeksAndDueDate();
        }, 300);
    }

    private updateBornPrematureWeeksAndDueDate(): void {
        this.mutateState(s => {
            if (!isNaN(this.debouncedBornPrematureValue) && this.debouncedBornPrematureValue > 0 && this.debouncedBornPrematureValue < 40) {
                s.bornPrematureWeeks = this.debouncedBornPrematureValue;
                s.dueDate = this.getDueDateByBornPremarureWeeks(this.debouncedBornPrematureValue);
            }
            else {
                s.bornPrematureWeeks = null;
                s.dueDate = null;
            }
        });
    }

    private clearComponentTimeOut(): void {
        if (this.debouncedBornPrematureTimeOut) {
            clearTimeout(this.debouncedBornPrematureTimeOut);
            this.debouncedBornPrematureTimeOut = null;
        }
    }

    private renderPatientErrors(): JSX.Element {
        if (!this.props.hasValidPatientGender) {
            return (
                <div className="warning">
                    {cacheStore.GetString("GrowthChartsNoSexAtBirthErrorLabel")}
                </div>
            );
        }
        if (!this.props.patientDateOfBirth) {
            return (
                <div className="warning">
                    {cacheStore.GetString("GrowthChartNoDateOfBirthWarningLabel")}
                </div>
            );
        }

        return null;
    }
}

export { GrowthChartsSettingsBar };
